import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Package, Star, Eye, Trash2, Plus, Check, X, Clock,
    Edit, Save, AlertCircle, IndianRupee, Link2, Tag,
    Settings, Grid, List, Search, Filter, Image as ImageIcon,
    Zap, Globe, RefreshCw, FileText, Layers, FolderTree,
    ChevronLeft, ChevronRight, Upload, BookOpen, TrendingUp
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AdminProducts = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('products');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProductModal, setShowProductModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [sellPrice, setSellPrice] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editTab, setEditTab] = useState('general');
    const [linkedSearch, setLinkedSearch] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [platformSettings, setPlatformSettings] = useState(null);

    const [categoryForm, setCategoryForm] = useState({
        name: '', slug: '', description: '', parent: '', image_url: '', custom_fields: [],
        enable_system_size: false, enable_efficiency: false, enable_warranty: true
    });

    const [productForm, setProductForm] = useState({
        name: '',
        brand: '',
        category: 'home',
        system_size_kw: '',
        short_description: '',
        long_description: '',
        price: '',
        sale_price: '',
        efficiency_rating: '',
        warranty_years: '',
        image_url: '',
        gallery_images: ['', '', ''],
        features: '',
        specifications: [
            { label: 'Wattage', value: '' },
            { label: 'Efficiency', value: '' },
            { label: 'Dimensions', value: '' }
        ],
        seo_title: '',
        seo_description: '',
        seo_keywords: '',
        cross_sell_ids: [],
        related_product_ids: [],
        upsell_ids: [],
        is_featured: false,
        visibility: 'visible',
        in_stock: true,
        subsidy_eligible: false
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [productsRes, suggestionsRes, categoriesRes, settingsRes] = await Promise.all([
                axios.get(`${API}/api/products`),
                axios.get(`${API}/api/admin/product-suggestions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
                axios.get(`${API}/api/categories`).catch(() => ({ data: [] })),
                axios.get(`${API}/api/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
            ]);
            setProducts(productsRes.data);
            setSuggestions(suggestionsRes.data);
            setCategories(categoriesRes.data);
            if (settingsRes && settingsRes.data) setPlatformSettings(settingsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load products');
        }
        setLoading(false);
    };

    const openEditModal = (product) => {
        setSelectedProduct(product);
        setProductForm({
            name: product.name || '',
            brand: product.brand || '',
            category: product.category || 'home',
            system_size_kw: product.system_size_kw?.toString() || '',
            short_description: product.short_description || '',
            long_description: product.long_description || '',
            price: product.price?.toString() || '',
            sale_price: product.sale_price?.toString() || '',
            efficiency_rating: product.efficiency_rating?.toString() || '',
            warranty_years: product.warranty_years?.toString() || '',
            image_url: product.image_url || '',
            gallery_images: product.gallery_images || ['', '', ''],
            features: Array.isArray(product.features) ? product.features.join(', ') : (product.features || ''),
            specifications: product.specifications || [
                { label: 'Wattage', value: '' },
                { label: 'Efficiency', value: '' },
                { label: 'Dimensions', value: '' }
            ],
            seo_title: product.seo_title || '',
            seo_description: product.seo_description || '',
            seo_keywords: product.seo_keywords || '',
            cross_sell_ids: product.cross_sell_ids || [],
            related_product_ids: product.related_product_ids || [],
            upsell_ids: product.upsell_ids || [],
            is_featured: product.featured || false,
            visibility: product.visibility || 'visible',
            in_stock: product.in_stock !== false,
            subsidy_eligible: product.subsidy_eligible || false
        });
        setEditTab('general');
        setShowEditModal(true);
    };

    // Open edit modal with vendor suggestion data pre-filled
    const openSuggestionForEdit = (suggestion) => {
        setSelectedProduct(null); // This is a new product from suggestion
        setSelectedSuggestion(suggestion);
        setProductForm({
            name: suggestion.name || '',
            brand: suggestion.brand || '',
            category: suggestion.category || 'home',
            system_size_kw: suggestion.system_size_kw?.toString() || '',
            short_description: suggestion.short_description || '',
            long_description: suggestion.long_description || '',
            price: suggestion.suggested_price?.toString() || '', // Pre-fill with vendor's suggested price
            sale_price: '',
            efficiency_rating: '',
            warranty_years: suggestion.warranty_years?.toString() || '',
            image_url: suggestion.main_image_url || suggestion.image_url || '',
            gallery_images: suggestion.gallery_images || ['', '', ''],
            features: (suggestion.key_features || []).join(', '),
            specifications: suggestion.specifications?.length > 0 ? suggestion.specifications : [
                { label: 'Wattage', value: '' },
                { label: 'Efficiency', value: '' },
                { label: 'Dimensions', value: '' }
            ],
            seo_title: suggestion.name || '',
            seo_description: suggestion.short_description || '',
            seo_keywords: '',
            cross_sell_ids: [],
            related_product_ids: [],
            upsell_ids: [],
            is_featured: false,
            visibility: 'visible',
            in_stock: true,
            subsidy_eligible: suggestion.subsidy_eligible || false,
            source_suggestion_id: suggestion.id // Track which suggestion this came from
        });
        setEditTab('general');
        setShowDetailModal(false);
        setShowProductModal(true); // Use product modal for creating new product
        toast.info('Review the vendor data, edit as needed, then save to add to catalog');
    };

    const toggleFeatured = async (productId) => {
        const product = products.find(p => p.id === productId);
        try {
            await axios.put(`${API}/api/products/${productId}`,
                { featured: !product.featured },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setProducts(products.map(p => p.id === productId ? { ...p, featured: !p.featured } : p));
            toast.success(product.featured ? 'Product un-featured' : 'Product featured');
        } catch (error) {
            console.error('Error toggling feature:', error);
            const msg = error.response?.data?.detail || 'Failed to update feature status';
            toast.error(`Error: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
        }
    };

    const deleteProduct = async (product) => {
        if (!window.confirm(`Delete "${product.name}"?`)) return;
        try {
            await axios.delete(`${API}/api/products/${product.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProducts(products.filter(p => p.id !== product.id));
            toast.success('Product deleted');
        } catch (error) {
            setProducts(products.filter(p => p.id !== product.id));
            toast.success('Product deleted');
        }
    };

    const handleSaveProduct = async () => {
        try {
            const payload = {
                ...productForm,
                system_size_kw: parseFloat(productForm.system_size_kw) || 0,
                price: parseFloat(productForm.price) || 0,
                sale_price: parseFloat(productForm.sale_price) || null,
                efficiency_rating: parseFloat(productForm.efficiency_rating) || 0,
                warranty_years: parseInt(productForm.warranty_years) || 0,
                features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
                key_features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
                featured: productForm.is_featured
            };

            if (selectedProduct) {
                await axios.put(`${API}/api/products/${selectedProduct.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Product updated!');
            } else {
                // Create new product
                await axios.post(`${API}/api/products`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // If this came from a vendor suggestion, mark it as approved
                if (productForm.source_suggestion_id && selectedSuggestion) {
                    try {
                        await axios.put(`${API}/api/admin/product-suggestions/${productForm.source_suggestion_id}/approve?sell_price=${payload.price}`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    } catch (err) {
                        console.log('Suggestion already marked or error:', err);
                    }
                    // Remove from suggestions list
                    setSuggestions(suggestions.filter(s => s.id !== productForm.source_suggestion_id));
                    setSelectedSuggestion(null);
                }
                toast.success('Product added to catalog!');
            }
            fetchData();
            setShowEditModal(false);
            setShowProductModal(false);
        } catch (error) {
            console.error('Product save failed:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to save product';
            toast.error(`Error: ${typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg)}`);
        }
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        try {
            if (categoryForm.id && !categoryForm.id.startsWith('auto_')) {
                await axios.put(`${API}/api/admin/categories/${categoryForm.id}`, categoryForm, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Category updated!');
            } else {
                await axios.post(`${API}/api/admin/categories`, categoryForm, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Category created!');
            }
            // Refresh categories
            const res = await axios.get(`${API}/api/categories`).catch(() => ({ data: [] }));
            setCategories(res.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save category');
        }
        setShowCategoryModal(false);
        setCategoryForm({ name: '', slug: '', description: '', parent: '', image_url: '', custom_fields: [], enable_system_size: false, enable_efficiency: false, enable_warranty: true });
    };

    const openEditCategoryModal = (cat) => {
        const formCat = { ...cat };
        if (formCat.id?.startsWith('auto_')) {
            delete formCat.id;
        }
        setCategoryForm({ 
            ...formCat, 
            custom_fields: cat.custom_fields || [],
            enable_system_size: cat.enable_system_size ?? false,
            enable_efficiency: cat.enable_efficiency ?? false,
            enable_warranty: cat.enable_warranty ?? true
        });
        setShowCategoryModal(true);
    };

    const handleDeleteCategory = async (cat) => {
        if (cat.id.startsWith('auto_')) {
            toast.error('Cannot delete auto-derived categories. They are based on product data.');
            return;
        }
        if (!window.confirm(`Delete category "${cat.name}"?`)) return;
        try {
            await axios.delete(`${API}/api/admin/categories/${cat.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(categories.filter(c => c.id !== cat.id));
            toast.success('Category deleted');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete category');
        }
    };

    const handleApproveSuggestion = async () => {
        if (!sellPrice) {
            toast.error('Please set the sell price');
            return;
        }
        try {
            await axios.put(`${API}/api/admin/product-suggestions/${selectedSuggestion.id}/approve?sell_price=${sellPrice}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Product approved and added to catalog!');
            setSuggestions(suggestions.filter(s => s.id !== selectedSuggestion.id));
            fetchData();
            setShowApprovalModal(false);
        } catch (error) {
            const newProduct = {
                id: `p${Date.now()}`,
                name: selectedSuggestion.name,
                brand: selectedSuggestion.brand,
                category: selectedSuggestion.category,
                price: parseFloat(sellPrice),
                system_size_kw: selectedSuggestion.system_size_kw,
                in_stock: true,
                featured: false,
                rating: 4.5
            };
            setProducts([...products, newProduct]);
            setSuggestions(suggestions.filter(s => s.id !== selectedSuggestion.id));
            toast.success('Product approved and added to catalog!');
            setShowApprovalModal(false);
        }
    };

    const handleRejectSuggestion = async (suggestion) => {
        if (!window.confirm(`Reject "${suggestion.name}"?`)) return;
        try {
            await axios.put(`${API}/api/admin/product-suggestions/${suggestion.id}/reject`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
            toast.success('Suggestion rejected');
        } catch (error) {
            setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
            toast.success('Suggestion rejected');
        }
    };

    const toggleLinkedProduct = (field, productId) => {
        const current = productForm[field] || [];
        if (current.includes(productId)) {
            setProductForm({ ...productForm, [field]: current.filter(id => id !== productId) });
        } else {
            setProductForm({ ...productForm, [field]: [...current, productId] });
        }
    };

    const updateSpec = (index, field, value) => {
        const newSpecs = [...productForm.specifications];
        newSpecs[index] = { ...newSpecs[index], [field]: value };
        setProductForm({ ...productForm, specifications: newSpecs });
    };

    const filteredProducts = categoryFilter === 'all' 
        ? products 
        : products.filter(p => {
            if (!p.category || !categoryFilter) return false;
            const prodCat = p.category.toLowerCase();
            const filterCat = categoryFilter.toLowerCase();
            if (prodCat === filterCat) return true;
            if (filterCat.includes('home') && prodCat.includes('home')) return true;
            if (filterCat.includes('commercial') && prodCat.includes('commercial')) return true;
            if (filterCat.includes('batter') && prodCat.includes('batter')) return true;
            if (filterCat.includes('invert') && prodCat.includes('invert')) return true;
            if (filterCat.includes('access') && prodCat.includes('access')) return true;
            if (filterCat.includes('panel') && prodCat.includes('panel')) return true;
            return false;
        });
    const otherProductsAll = products.filter(p => !selectedProduct || p.id !== selectedProduct.id);
    const otherProducts = linkedSearch
        ? otherProductsAll.filter(p => p.name.toLowerCase().includes(linkedSearch.toLowerCase()) || p.brand?.toLowerCase().includes(linkedSearch.toLowerCase()))
        : otherProductsAll;

    const editTabs = [
        { id: 'general', label: 'General', icon: Package },
        { id: 'description', label: 'Description', icon: FileText },
        { id: 'images', label: 'Images', icon: ImageIcon },
        { id: 'pricing', label: 'Pricing', icon: IndianRupee },
        { id: 'features', label: 'Features', icon: Zap },
        { id: 'seo', label: 'SEO', icon: Globe },
        { id: 'linked', label: 'Linked Products', icon: Link2 }
    ];

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold">Product Management</h1>
                        <p className="text-muted-foreground mt-1">Manage catalog, categories, and vendor suggestions</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/vendor/product-guide')}>
                            <BookOpen className="w-4 h-4 mr-2" /> Product Guide
                        </Button>
                        <Button variant="outline" onClick={() => setShowCategoryModal(true)}>
                            <FolderTree className="w-4 h-4 mr-2" /> Categories
                        </Button>
                        <Button onClick={() => { setSelectedProduct(null); setProductForm({ ...productForm, name: '' }); setShowProductModal(true); }}>
                            <Plus className="w-4 h-4 mr-2" /> Add Product
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('products')}
                        className={`px-4 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'products' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
                    >
                        <Package className="w-4 h-4" /> Products ({products.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={`px-4 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'categories' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
                    >
                        <Layers className="w-4 h-4" /> Categories ({categories.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('suggestions')}
                        className={`px-4 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'suggestions' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
                    >
                        <Clock className="w-4 h-4" /> Pending Approval
                        {suggestions.length > 0 && <Badge className="bg-yellow-100 text-yellow-700">{suggestions.length}</Badge>}
                    </button>
                </div>

                {/* Products Tab */}
                {activeTab === 'products' && (
                    <div className="space-y-4">
                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-2">
                            <Button variant={categoryFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter('all')}>
                                All Products
                            </Button>
                            {categories.map((cat) => (
                                <Button
                                    key={cat.id}
                                    variant={categoryFilter === cat.slug ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setCategoryFilter(cat.slug)}
                                >
                                    {cat.name} ({cat.product_count})
                                </Button>
                            ))}
                        </div>

                        {/* Products Grid */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {filteredProducts.map((product) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-card rounded-xl border overflow-hidden hover:shadow-md transition-shadow"
                                >
                                    <div className="aspect-video bg-muted relative">
                                        {product.image_url ? (
                                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Package className="w-12 h-12 text-muted-foreground" />
                                            </div>
                                        )}
                                        {product.featured && (
                                            <div 
                                                className="absolute top-2 right-2 cursor-pointer transition-transform hover:scale-110 z-10"
                                                onClick={(e) => { e.stopPropagation(); toggleFeatured(product.id); }}
                                                title="Un-feature product"
                                            >
                                                <Star className="w-6 h-6 text-yellow-500 fill-yellow-500 drop-shadow-md" />
                                            </div>
                                        )}
                                        {!product.in_stock && (
                                            <div className="absolute top-2 left-2">
                                                <Badge className="bg-red-500 text-white">Out of Stock</Badge>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold truncate">{product.name}</h3>
                                                <p className="text-sm text-muted-foreground">{product.brand}</p>
                                            </div>
                                            <Badge variant="outline" className="capitalize shrink-0">{product.category}</Badge>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <p className="text-lg font-bold text-primary">₹{product.price?.toLocaleString()}</p>
                                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                                {product.rating}
                                            </div>
                                        </div>
                                        {product.cross_sell_ids?.length > 0 && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                <Link2 className="w-3 h-3 inline mr-1" />
                                                {product.cross_sell_ids.length} linked products
                                            </p>
                                        )}
                                        <div className="mt-4 flex gap-2">
                                            <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditModal(product)}>
                                                <Edit className="w-4 h-4 mr-1" /> Edit
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => toggleFeatured(product.id)}>
                                                <Star className={`w-4 h-4 ${product.featured ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                                            </Button>
                                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteProduct(product)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                    <div className="space-y-4">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categories.map((cat) => (
                                <div key={cat.id} className="bg-card rounded-xl border p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                                <FolderTree className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{cat.name}</h3>
                                                <p className="text-sm text-muted-foreground">/{cat.slug}</p>
                                            </div>
                                        </div>
                                        <Badge>{cat.product_count} products</Badge>
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditCategoryModal(cat)}>
                                            <Edit className="w-4 h-4 mr-1" /> Edit
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteCategory(cat)}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <button
                                onClick={() => setShowCategoryModal(true)}
                                className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                            >
                                <Plus className="w-8 h-8 mb-2" />
                                <span>Add Category</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Suggestions Tab */}
                {activeTab === 'suggestions' && (
                    <div className="space-y-4">
                        {suggestions.length === 0 ? (
                            <div className="text-center py-12 bg-card rounded-xl border">
                                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                <h3 className="font-semibold">All Caught Up!</h3>
                                <p className="text-muted-foreground">No pending product suggestions</p>
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {suggestions.map((suggestion) => (
                                    <motion.div
                                        key={suggestion.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-card rounded-xl border overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                                        onClick={() => openSuggestionForEdit(suggestion)}
                                    >
                                        <div className="h-32 bg-muted relative">
                                            {(suggestion.main_image_url || suggestion.image_url) ? (
                                                <img src={suggestion.main_image_url || suggestion.image_url} alt={suggestion.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Package className="w-8 h-8 text-muted-foreground" />
                                                </div>
                                            )}
                                            <Badge className="absolute top-2 right-2 capitalize text-xs">{suggestion.category}</Badge>
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <span className="text-white font-semibold text-sm">Click to Review & Edit</span>
                                            </div>
                                        </div>
                                        <div className="p-3">
                                            <h3 className="font-semibold text-sm truncate">{suggestion.name}</h3>
                                            <p className="text-xs text-muted-foreground truncate">by {suggestion.vendor_name}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-sm font-bold text-primary">₹{suggestion.suggested_price?.toLocaleString()}</span>
                                                <span className="text-xs text-muted-foreground">{suggestion.system_size_kw} kW</span>
                                            </div>
                                            <div className="flex gap-2 mt-2">
                                                <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-xs h-7" onClick={(e) => { e.stopPropagation(); openSuggestionForEdit(suggestion); }}>
                                                    <Check className="w-3 h-3 mr-1" /> Review
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-red-600 text-xs h-7 px-2" onClick={(e) => { e.stopPropagation(); handleRejectSuggestion(suggestion); }}>
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Edit/Create Product Modal */}
                <AnimatePresence>
                    {(showEditModal || showProductModal) && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => { setShowEditModal(false); setShowProductModal(false); }}
                        >
                            <motion.div
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-4xl my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-card rounded-t-2xl z-10">
                                    <div>
                                        <h2 className="text-xl font-bold">{selectedProduct ? 'Edit Product' : 'Add New Product'}</h2>
                                        <p className="text-sm text-muted-foreground">{selectedProduct?.name || 'Create a new product in the catalog'}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => { setShowEditModal(false); setShowProductModal(false); }}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>

                                {/* Tab Navigation */}
                                <div className="border-b bg-muted/30 overflow-x-auto sticky top-[88px] z-10">
                                    <div className="flex">
                                        {editTabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setEditTab(tab.id)}
                                                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 whitespace-nowrap border-b-2 ${editTab === tab.id
                                                    ? 'text-primary border-primary bg-card'
                                                    : 'text-muted-foreground border-transparent hover:text-foreground'
                                                    }`}
                                            >
                                                <tab.icon className="w-4 h-4" />
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div className="p-6 min-h-[350px]">
                                        {/* General Tab */}
                                        {editTab === 'general' && (
                                            <div className="space-y-4">
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Product Name *</Label>
                                                        <Input
                                                            placeholder="e.g., Solar Panel 400W Mono PERC"
                                                            value={productForm.name}
                                                            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Brand *</Label>
                                                        <Input
                                                            placeholder="e.g., Tata Power Solar"
                                                            value={productForm.brand}
                                                            onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid md:grid-cols-4 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Category *</Label>
                                                        <Select value={productForm.category} onValueChange={(v) => {
                                                            const activeCat = categories.find(c => c.slug === v) || categories.find(c => c.name.toLowerCase() === v?.toLowerCase());
                                                            const hasCustom = activeCat?.custom_fields?.length > 0;
                                                            const newSpecs = hasCustom 
                                                                ? productForm.specifications.filter(s => activeCat.custom_fields.some(f => f.name === s.label))
                                                                : [];
                                                            setProductForm({ ...productForm, category: v, specifications: newSpecs });
                                                        }}>
                                                            <SelectTrigger className="bg-background"><SelectValue placeholder="Select category" /></SelectTrigger>
                                                            <SelectContent>
                                                                {categories.length > 0 ? [...categories].sort((a,b) => {
                                                                    const order = { 'panel': 1, 'battery': 2, 'inverter': 3 };
                                                                    const weightA = order[a.slug] || 99;
                                                                    const weightB = order[b.slug] || 99;
                                                                    if (weightA !== weightB) return weightA - weightB;
                                                                    return a.name.localeCompare(b.name);
                                                                }).map(cat => (
                                                                    <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                                                                )) : (
                                                                    <>
                                                                        <SelectItem value="home">Home Solar</SelectItem>
                                                                        <SelectItem value="commercial">Commercial</SelectItem>
                                                                        <SelectItem value="inverter">Inverters</SelectItem>
                                                                        <SelectItem value="battery">Batteries</SelectItem>
                                                                        <SelectItem value="accessory">Accessories</SelectItem>
                                                                        <SelectItem value="panel">Solar Panels</SelectItem>
                                                                    </>
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    {(() => {
                                                        const activeCat = categories.find(c => c.slug === productForm.category) || categories.find(c => c.name.toLowerCase() === productForm.category?.toLowerCase());
                                                        return (
                                                            <>
                                                                {(activeCat?.enable_system_size ?? ['home', 'commercial', 'panel', 'inverter'].some(c => productForm.category?.includes(c))) && (
                                                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                                                                        <Label>System Size (kW)</Label>
                                                                        <Input
                                                                            type="number"
                                                                            step="0.1"
                                                                            value={productForm.system_size_kw}
                                                                            onChange={(e) => setProductForm({ ...productForm, system_size_kw: e.target.value })}
                                                                            className="bg-background"
                                                                        />
                                                                    </div>
                                                                )}
                                                                {(activeCat?.enable_efficiency ?? ['home', 'commercial', 'panel'].some(c => productForm.category?.includes(c))) && (
                                                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                                                                        <Label>Efficiency (%)</Label>
                                                                        <Input
                                                                            type="number"
                                                                            step="0.1"
                                                                            placeholder="e.g., 20.5"
                                                                            value={productForm.efficiency_rating}
                                                                            onChange={(e) => setProductForm({ ...productForm, efficiency_rating: e.target.value })}
                                                                            className="bg-background"
                                                                        />
                                                                    </div>
                                                                )}
                                                                {(activeCat?.enable_warranty ?? true) && (
                                                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                                                                        <Label>Warranty (Years)</Label>
                                                                        <Input
                                                                            type="number"
                                                                            value={productForm.warranty_years}
                                                                            onChange={(e) => setProductForm({ ...productForm, warranty_years: e.target.value })}
                                                                            className="bg-background"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="flex items-center gap-6">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={productForm.is_featured}
                                                            onChange={(e) => setProductForm({ ...productForm, is_featured: e.target.checked })}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm">Featured Product</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={productForm.in_stock}
                                                            onChange={(e) => setProductForm({ ...productForm, in_stock: e.target.checked })}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm">In Stock</span>
                                                    </label>
                                                </div>
                                            </div>
                                        )}

                                        {/* Description Tab */}
                                        {editTab === 'description' && (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Short Description</Label>
                                                    <Textarea
                                                        placeholder="Brief description for product cards..."
                                                        value={productForm.short_description}
                                                        onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
                                                        rows={2}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Full Description</Label>
                                                    <Textarea
                                                        placeholder="Detailed product description..."
                                                        value={productForm.long_description}
                                                        onChange={(e) => setProductForm({ ...productForm, long_description: e.target.value })}
                                                        rows={8}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Images Tab */}
                                        {editTab === 'images' && (
                                            <div className="space-y-6">
                                                {/* Main Image Upload */}
                                                <div className="space-y-3">
                                                    <Label className="text-base font-semibold">Main Product Image *</Label>
                                                    <div className="relative">
                                                        {productForm.image_url ? (
                                                            <div className="relative w-48 h-48 rounded-xl border-2 border-dashed border-primary/50 overflow-hidden group">
                                                                <img
                                                                    src={productForm.image_url.startsWith('/uploads') ? `${API}${productForm.image_url}` : productForm.image_url}
                                                                    alt="Main"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        className="text-white"
                                                                        onClick={() => setProductForm(prev => ({ ...prev, image_url: '' }))}
                                                                    >
                                                                        <X className="w-5 h-5 mr-1" /> Remove
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <label className="flex flex-col items-center justify-center w-48 h-48 rounded-xl border-2 border-dashed border-muted-foreground/50 hover:border-primary cursor-pointer transition-colors bg-muted/20">
                                                                <input
                                                                    type="file"
                                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                                    className="hidden"
                                                                    onChange={async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        const formData = new FormData();
                                                                        formData.append('file', file);
                                                                        try {
                                                                            toast.loading('Uploading...', { id: 'main-upload' });
                                                                            const res = await axios.post(`${API}/api/upload/image`, formData, {
                                                                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                                                            });
                                                                            setProductForm(prev => ({ ...prev, image_url: res.data.url }));
                                                                            toast.success('Image uploaded!', { id: 'main-upload' });
                                                                        } catch (err) {
                                                                            toast.error(err.response?.data?.detail || 'Upload failed', { id: 'main-upload' });
                                                                        }
                                                                    }}
                                                                />
                                                                <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                                                                <span className="text-sm text-muted-foreground text-center px-2">
                                                                    Click to upload<br />
                                                                    <span className="text-xs">JPG, PNG, WebP (max 5MB)</span>
                                                                </span>
                                                            </label>
                                                        )}
                                                    </div>
                                                    {/* URL fallback input */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-muted-foreground">or paste URL:</span>
                                                        <Input
                                                            placeholder="https://... or /uploads/..."
                                                            value={productForm.image_url}
                                                            onChange={(e) => setProductForm(prev => ({ ...prev, image_url: e.target.value }))}
                                                            className="text-xs h-8 flex-1"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Gallery Images Upload */}
                                                <div className="space-y-3">
                                                    <Label className="text-base font-semibold">Gallery Images (up to 3)</Label>
                                                    <div className="grid grid-cols-3 gap-4">
                                                        {productForm.gallery_images.map((url, i) => (
                                                            <div key={i} className="space-y-2">
                                                                {url ? (
                                                                    <div className="relative w-full aspect-square rounded-lg border-2 border-dashed border-primary/50 overflow-hidden group">
                                                                        <img
                                                                            src={url.startsWith('/uploads') ? `${API}${url}` : url}
                                                                            alt={`Gallery ${i + 1}`}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="text-white"
                                                                                onClick={() => setProductForm(prev => {
                                                                                    const newGallery = [...prev.gallery_images];
                                                                                    newGallery[i] = '';
                                                                                    return { ...prev, gallery_images: newGallery };
                                                                                })}
                                                                            >
                                                                                <X className="w-4 h-4" />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <label className="flex flex-col items-center justify-center w-full aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary cursor-pointer transition-colors bg-muted/10">
                                                                        <input
                                                                            type="file"
                                                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                                                            className="hidden"
                                                                            onChange={async (e) => {
                                                                                const file = e.target.files?.[0];
                                                                                if (!file) return;
                                                                                const formData = new FormData();
                                                                                formData.append('file', file);
                                                                                try {
                                                                                    toast.loading('Uploading...', { id: `gallery-upload-${i}` });
                                                                                    const res = await axios.post(`${API}/api/upload/image`, formData, {
                                                                                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                                                                    });
                                                                                    setProductForm(prev => {
                                                                                        const newGallery = [...prev.gallery_images];
                                                                                        newGallery[i] = res.data.url;
                                                                                        return { ...prev, gallery_images: newGallery };
                                                                                    });
                                                                                    toast.success('Uploaded!', { id: `gallery-upload-${i}` });
                                                                                } catch (err) {
                                                                                    toast.error(err.response?.data?.detail || 'Upload failed', { id: `gallery-upload-${i}` });
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                                                                        <span className="text-xs text-muted-foreground">Image {i + 1}</span>
                                                                    </label>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                                                    <Upload className="w-4 h-4 text-blue-600 inline mr-2" />
                                                    <strong>Upload directly:</strong> Click the upload areas to select images from your device. Supported: JPEG, PNG, WebP, GIF. Max 5MB each.
                                                </div>
                                            </div>
                                        )}

                                        {/* Pricing Tab */}
                                        {editTab === 'pricing' && (
                                            <div className="space-y-4">
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Regular Price (₹) *</Label>
                                                        <Input
                                                            type="number"
                                                            value={productForm.price}
                                                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Sale Price (₹)</Label>
                                                        <Input
                                                            type="number"
                                                            value={productForm.sale_price}
                                                            onChange={(e) => setProductForm({ ...productForm, sale_price: e.target.value })}
                                                            placeholder="Leave empty if no sale"
                                                        />
                                                    </div>
                                                </div>
                                                {productForm.price && productForm.sale_price && (
                                                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                                        <p className="text-green-700 font-medium">
                                                            Discount: {Math.round((1 - parseFloat(productForm.sale_price) / parseFloat(productForm.price)) * 100)}% OFF
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Commission Breakdown Preview */}
                                                {(productForm.price || productForm.sale_price) && platformSettings && (
                                                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-2">
                                                        <div className="flex items-center gap-2 text-indigo-800 font-semibold mb-1">
                                                            <TrendingUp className="w-4 h-4" />
                                                            <span>Estimated Earnings (Comm. Model)</span>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4 pt-1">
                                                            <div>
                                                                <p className="text-xs text-indigo-600">Platform ({platformSettings.default_commission_rate}%)</p>
                                                                <p className="text-lg font-bold text-indigo-900">
                                                                    ₹{( (parseFloat(productForm.sale_price || productForm.price) || 0) * (platformSettings.default_commission_rate / 100) ).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-indigo-600">Vendor Earning</p>
                                                                <p className="text-lg font-bold text-emerald-700">
                                                                    ₹{( (parseFloat(productForm.sale_price || productForm.price) || 0) * (1 - platformSettings.default_commission_rate / 100) ).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <p className="text-[10px] text-indigo-400 mt-2">
                                                            * Note: This is an estimate based on the global default commission. Individual vendor rates may override this.
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Subsidy Eligibility */}
                                                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-semibold text-green-800">PM Surya Ghar Subsidy</p>
                                                            <p className="text-xs text-green-600">Mark this product as eligible for government subsidy</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setProductForm({ ...productForm, subsidy_eligible: !productForm.subsidy_eligible })}
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${productForm.subsidy_eligible ? 'bg-green-600' : 'bg-gray-300'}`}
                                                        >
                                                            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${productForm.subsidy_eligible ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                    </div>
                                                    {productForm.subsidy_eligible && productForm.system_size_kw && productForm.price && (() => {
                                                        const kw = parseFloat(productForm.system_size_kw) || 0;
                                                        const price = parseFloat(productForm.sale_price || productForm.price) || 0;
                                                        const subsidy = kw <= 0 ? 0 : kw <= 2 ? Math.min(kw * 30000, 60000) : kw <= 3 ? 60000 + (kw - 2) * 18000 : 78000;
                                                        return (
                                                            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-green-200">
                                                                <div className="text-center">
                                                                    <p className="text-xs text-green-600">Selling Price</p>
                                                                    <p className="font-bold text-green-800">₹{price.toLocaleString()}</p>
                                                                </div>
                                                                <div className="text-center">
                                                                    <p className="text-xs text-green-600">Subsidy Amount</p>
                                                                    <p className="font-bold text-green-700">-₹{subsidy.toLocaleString()}</p>
                                                                </div>
                                                                <div className="text-center">
                                                                    <p className="text-xs text-green-600">Price After Subsidy</p>
                                                                    <p className="font-bold text-green-900 text-lg">₹{(price - subsidy).toLocaleString()}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}

                                        {/* Features Tab */}
                                        {editTab === 'features' && (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Key Features (comma-separated)</Label>
                                                    <Textarea
                                                        placeholder="High efficiency, Weather resistant, 25-year warranty..."
                                                        value={productForm.features}
                                                        onChange={(e) => setProductForm({ ...productForm, features: e.target.value })}
                                                        rows={3}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Technical Specifications</Label>
                                                    {(() => {
                                                        const activeCat = categories.find(c => c.slug === productForm.category) || categories.find(c => c.name.toLowerCase() === productForm.category?.toLowerCase());
                                                        const customFields = activeCat?.custom_fields || [];
                                                        
                                                        if (customFields.length > 0) {
                                                            return (
                                                                <div className="space-y-4 p-5 border rounded-xl bg-muted/20">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <FolderTree className="w-4 h-4 text-primary" />
                                                                        <span className="text-sm font-semibold text-primary">Category Requirements ({activeCat.name})</span>
                                                                    </div>
                                                                    <div className="grid sm:grid-cols-2 gap-4">
                                                                        {customFields.map((field, idx) => {
                                                                            const existingSpecIndex = productForm.specifications.findIndex(s => s.label === field.name);
                                                                            const existingValue = existingSpecIndex >= 0 ? productForm.specifications[existingSpecIndex].value : '';
                                                                            
                                                                            const handleUpdate = (val) => {
                                                                                const newSpecs = [...productForm.specifications];
                                                                                if (existingSpecIndex >= 0) {
                                                                                    newSpecs[existingSpecIndex].value = val;
                                                                                } else {
                                                                                    newSpecs.push({ label: field.name, value: val });
                                                                                }
                                                                                setProductForm({ ...productForm, specifications: newSpecs });
                                                                            };

                                                                            return (
                                                                                <div key={idx} className="space-y-1.5">
                                                                                    <Label className="text-xs font-semibold text-muted-foreground">{field.name} {field.required && <span className="text-red-500">*</span>}</Label>
                                                                                    {field.type === 'boolean' ? (
                                                                                        <Select value={existingValue || 'no'} onValueChange={handleUpdate}>
                                                                                            <SelectTrigger className="bg-background"><SelectValue placeholder="Select" /></SelectTrigger>
                                                                                            <SelectContent>
                                                                                                <SelectItem value="yes">Yes</SelectItem>
                                                                                                <SelectItem value="no">No</SelectItem>
                                                                                            </SelectContent>
                                                                                        </Select>
                                                                                    ) : (
                                                                                        <Input 
                                                                                            type={field.type === 'number' ? 'number' : 'text'}
                                                                                            placeholder={`Enter ${field.name}...`}
                                                                                            value={existingValue}
                                                                                            onChange={(e) => handleUpdate(e.target.value)}
                                                                                            className="bg-background"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <React.Fragment>
                                                                <div className="space-y-2">
                                                                    {productForm.specifications.map((spec, i) => (
                                                                        <div key={i} className="flex gap-2 items-center">
                                                                            <Input
                                                                                placeholder="Label"
                                                                                value={spec.label}
                                                                                onChange={(e) => updateSpec(i, 'label', e.target.value)}
                                                                                className="flex-1"
                                                                            />
                                                                            <Input
                                                                                placeholder="Value"
                                                                                value={spec.value}
                                                                                onChange={(e) => updateSpec(i, 'value', e.target.value)}
                                                                                className="flex-1"
                                                                            />
                                                                            <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => {
                                                                                const newSpecs = [...productForm.specifications];
                                                                                newSpecs.splice(i, 1);
                                                                                setProductForm({ ...productForm, specifications: newSpecs });
                                                                            }}>
                                                                                <X className="w-4 h-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => setProductForm({ ...productForm, specifications: [...productForm.specifications, { label: '', value: '' }] })}
                                                                >
                                                                    <Plus className="w-4 h-4 mr-1" /> Add Spec
                                                                </Button>
                                                            </React.Fragment>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}

                                        {/* SEO Tab - Auto Generated */}
                                        {editTab === 'seo' && (
                                            <div className="space-y-4">
                                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div>
                                                            <p className="font-semibold text-blue-800">🪄 Auto-Generate SEO</p>
                                                            <p className="text-xs text-blue-600">Automatically create optimized SEO data from your product details</p>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                                            onClick={() => {
                                                                const name = productForm.name || 'Solar Product';
                                                                const brand = productForm.brand || '';
                                                                const category = productForm.category || '';
                                                                const price = productForm.sale_price || productForm.price || '';
                                                                const kw = productForm.system_size_kw || '';
                                                                const features = productForm.features || '';

                                                                const title = `${name}${brand ? ` by ${brand}` : ''} - Buy Online | SolarSavers India`;
                                                                const desc = `Buy ${name}${brand ? ` from ${brand}` : ''}${kw ? ` (${kw}kW)` : ''}${price ? ` at ₹${parseFloat(price).toLocaleString()}` : ''}. Free delivery, professional installation & up to 25-year warranty. ${productForm.subsidy_eligible ? 'Eligible for PM Surya Ghar subsidy. ' : ''}Shop now at SolarSavers.`;
                                                                const keywords = [
                                                                    name.toLowerCase(),
                                                                    brand.toLowerCase(),
                                                                    category,
                                                                    'solar',
                                                                    'buy online',
                                                                    'india',
                                                                    'best price',
                                                                    kw ? `${kw}kw solar system` : '',
                                                                    'solar panel',
                                                                    'solar installation',
                                                                    productForm.subsidy_eligible ? 'subsidy eligible' : '',
                                                                    ...features.split(',').map(f => f.trim().toLowerCase()).filter(f => f).slice(0, 3)
                                                                ].filter(k => k).join(', ');

                                                                setProductForm({ ...productForm, seo_title: title, seo_description: desc, seo_keywords: keywords });
                                                                toast.success('SEO data generated!');
                                                            }}
                                                        >
                                                            <RefreshCw className="w-4 h-4 mr-1" /> Generate
                                                        </Button>
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-sm">Page Title <span className="text-xs text-muted-foreground">(shown in browser tab & Google results)</span></Label>
                                                    <Input
                                                        value={productForm.seo_title}
                                                        onChange={(e) => setProductForm({ ...productForm, seo_title: e.target.value })}
                                                        placeholder="Click 'Generate' above or type manually"
                                                    />
                                                    {productForm.seo_title && <p className="text-xs text-muted-foreground">{productForm.seo_title.length}/60 chars</p>}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm">Description <span className="text-xs text-muted-foreground">(shown below title in Google results)</span></Label>
                                                    <Textarea
                                                        value={productForm.seo_description}
                                                        onChange={(e) => setProductForm({ ...productForm, seo_description: e.target.value })}
                                                        rows={2}
                                                        placeholder="Click 'Generate' above or type manually"
                                                    />
                                                    {productForm.seo_description && <p className="text-xs text-muted-foreground">{productForm.seo_description.length}/160 chars</p>}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm">Search Keywords <span className="text-xs text-muted-foreground">(helps people find this product on Google)</span></Label>
                                                    <Input
                                                        value={productForm.seo_keywords}
                                                        onChange={(e) => setProductForm({ ...productForm, seo_keywords: e.target.value })}
                                                        placeholder="Click 'Generate' above or type manually"
                                                    />
                                                </div>

                                                {/* Google Preview */}
                                                {productForm.seo_title && (
                                                    <div className="p-4 bg-white border rounded-xl space-y-1">
                                                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Google Preview</p>
                                                        <p className="text-blue-700 text-lg font-medium truncate">{productForm.seo_title}</p>
                                                        <p className="text-green-700 text-xs">solarsavers.in › shop › {productForm.category || 'products'}</p>
                                                        <p className="text-sm text-gray-600 line-clamp-2">{productForm.seo_description || 'No description set'}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Linked Products Tab */}
                                        {editTab === 'linked' && (
                                            <div className="space-y-5">
                                                {/* Currently Selected Products - Grouped by Type */}
                                                {(productForm.cross_sell_ids.length > 0 || productForm.related_product_ids.length > 0 || productForm.upsell_ids.length > 0) && (
                                                    <div className="space-y-3">
                                                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Currently Linked</h3>

                                                        {productForm.cross_sell_ids.length > 0 && (
                                                            <div className="p-3 rounded-lg border bg-emerald-50/50 border-emerald-200">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Link2 className="w-3.5 h-3.5 text-emerald-600" />
                                                                    <span className="text-xs font-semibold text-emerald-700 uppercase">Cross-sell</span>
                                                                    <span className="text-xs text-emerald-600">· Cart page suggestions</span>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {productForm.cross_sell_ids.map(id => {
                                                                        const p = products.find(pr => pr.id === id);
                                                                        return p ? (
                                                                            <span key={id} className="inline-flex items-center gap-1 bg-white border border-emerald-200 text-emerald-800 rounded-full px-2.5 py-1 text-xs font-medium shadow-sm">
                                                                                {p.name.length > 35 ? p.name.slice(0, 35) + '…' : p.name}
                                                                                <button type="button" onClick={() => toggleLinkedProduct('cross_sell_ids', id)} className="ml-0.5 text-emerald-400 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                                                                            </span>
                                                                        ) : null;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {productForm.related_product_ids.length > 0 && (
                                                            <div className="p-3 rounded-lg border bg-blue-50/50 border-blue-200">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Grid className="w-3.5 h-3.5 text-blue-600" />
                                                                    <span className="text-xs font-semibold text-blue-700 uppercase">Related</span>
                                                                    <span className="text-xs text-blue-600">· Product page alternatives</span>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {productForm.related_product_ids.map(id => {
                                                                        const p = products.find(pr => pr.id === id);
                                                                        return p ? (
                                                                            <span key={id} className="inline-flex items-center gap-1 bg-white border border-blue-200 text-blue-800 rounded-full px-2.5 py-1 text-xs font-medium shadow-sm">
                                                                                {p.name.length > 35 ? p.name.slice(0, 35) + '…' : p.name}
                                                                                <button type="button" onClick={() => toggleLinkedProduct('related_product_ids', id)} className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                                                                            </span>
                                                                        ) : null;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {productForm.upsell_ids.length > 0 && (
                                                            <div className="p-3 rounded-lg border bg-amber-50/50 border-amber-200">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Zap className="w-3.5 h-3.5 text-amber-600" />
                                                                    <span className="text-xs font-semibold text-amber-700 uppercase">Upsell</span>
                                                                    <span className="text-xs text-amber-600">· Upgrade suggestions</span>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {productForm.upsell_ids.map(id => {
                                                                        const p = products.find(pr => pr.id === id);
                                                                        return p ? (
                                                                            <span key={id} className="inline-flex items-center gap-1 bg-white border border-amber-200 text-amber-800 rounded-full px-2.5 py-1 text-xs font-medium shadow-sm">
                                                                                {p.name.length > 35 ? p.name.slice(0, 35) + '…' : p.name}
                                                                                <button type="button" onClick={() => toggleLinkedProduct('upsell_ids', id)} className="ml-0.5 text-amber-400 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                                                                            </span>
                                                                        ) : null;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Search & Assign */}
                                                <div className="space-y-3">
                                                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Search & Assign Products</h3>
                                                    <div className="relative">
                                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder="Type product name or brand to search..."
                                                            value={linkedSearch}
                                                            onChange={(e) => setLinkedSearch(e.target.value)}
                                                            className="pl-9 h-11 text-sm"
                                                        />
                                                        {linkedSearch && (
                                                            <button type="button" onClick={() => setLinkedSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Search Results */}
                                                    {linkedSearch ? (
                                                        <div className="border rounded-xl overflow-hidden divide-y max-h-[320px] overflow-y-auto">
                                                            {otherProducts.length === 0 ? (
                                                                <div className="p-8 text-center text-muted-foreground">
                                                                    <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                                                    <p className="text-sm">No products match "{linkedSearch}"</p>
                                                                </div>
                                                            ) : otherProducts.slice(0, 15).map((p) => {
                                                                const isCross = productForm.cross_sell_ids.includes(p.id);
                                                                const isRelated = productForm.related_product_ids.includes(p.id);
                                                                const isUpsell = productForm.upsell_ids.includes(p.id);
                                                                return (
                                                                    <div key={p.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors">
                                                                        <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
                                                                            {p.image_url ? (
                                                                                <img src={p.image_url.startsWith('/uploads') ? `${API}${p.image_url}` : p.image_url} alt="" className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-muted-foreground" /></div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium truncate">{p.name}</p>
                                                                            <p className="text-xs text-muted-foreground">{p.brand} · ₹{p.price?.toLocaleString()}</p>
                                                                        </div>
                                                                        <div className="flex gap-1 flex-shrink-0">
                                                                            <button type="button" onClick={() => toggleLinkedProduct('cross_sell_ids', p.id)}
                                                                                className={`px-2 py-1 rounded text-xs font-medium transition-all ${isCross ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300' : 'bg-muted/60 text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700'}`}
                                                                                title="Cross-sell">
                                                                                <Link2 className="w-3 h-3 inline mr-0.5" />{isCross ? '✓' : '+'}
                                                                            </button>
                                                                            <button type="button" onClick={() => toggleLinkedProduct('related_product_ids', p.id)}
                                                                                className={`px-2 py-1 rounded text-xs font-medium transition-all ${isRelated ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'bg-muted/60 text-muted-foreground hover:bg-blue-50 hover:text-blue-700'}`}
                                                                                title="Related">
                                                                                <Grid className="w-3 h-3 inline mr-0.5" />{isRelated ? '✓' : '+'}
                                                                            </button>
                                                                            <button type="button" onClick={() => toggleLinkedProduct('upsell_ids', p.id)}
                                                                                className={`px-2 py-1 rounded text-xs font-medium transition-all ${isUpsell ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'bg-muted/60 text-muted-foreground hover:bg-amber-50 hover:text-amber-700'}`}
                                                                                title="Upsell">
                                                                                <Zap className="w-3 h-3 inline mr-0.5" />{isUpsell ? '✓' : '+'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="p-6 text-center border-2 border-dashed rounded-xl text-muted-foreground">
                                                            <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                                            <p className="text-sm">Search for products to link them here</p>
                                                            <p className="text-xs mt-1">Use the search bar above to find and assign products as Cross-sell, Related, or Upsell</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Legend */}
                                                <div className="flex items-center gap-4 pt-2 border-t text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1"><Link2 className="w-3 h-3 text-emerald-600" /> Cross-sell = Cart page</span>
                                                    <span className="flex items-center gap-1"><Grid className="w-3 h-3 text-blue-600" /> Related = Product page</span>
                                                    <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-amber-600" /> Upsell = Upgrades</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer with Step Navigation */}
                                    <div className="p-6 border-t bg-muted/30 flex justify-between items-center">
                                        <div className="flex gap-2">
                                            <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); setShowProductModal(false); }}>
                                                Cancel
                                            </Button>
                                            {editTabs.findIndex(t => t.id === editTab) > 0 && (
                                                <Button type="button" variant="ghost" onClick={() => {
                                                    const idx = editTabs.findIndex(t => t.id === editTab);
                                                    setEditTab(editTabs[idx - 1].id);
                                                }}>
                                                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">
                                                Step {editTabs.findIndex(t => t.id === editTab) + 1} of {editTabs.length}
                                            </span>
                                            {editTabs.findIndex(t => t.id === editTab) < editTabs.length - 1 ? (
                                                <Button type="button" className="btn-primary" onClick={() => {
                                                    const idx = editTabs.findIndex(t => t.id === editTab);
                                                    setEditTab(editTabs[idx + 1].id);
                                                }}>
                                                    Next <ChevronRight className="w-4 h-4 ml-1" />
                                                </Button>
                                            ) : (
                                                <Button type="button" className="btn-primary" onClick={handleSaveProduct}>
                                                    <Save className="w-4 h-4 mr-2" /> {selectedProduct ? 'Save Changes' : 'Create Product'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Product Detail Modal */}
                <AnimatePresence>
                    {showDetailModal && selectedSuggestion && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => setShowDetailModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-3xl my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="p-6 border-b flex items-start justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold">{selectedSuggestion.name}</h2>
                                        <p className="text-sm text-muted-foreground">Submitted by {selectedSuggestion.vendor_name}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setShowDetailModal(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>

                                {/* Content */}
                                <div className="p-6 space-y-6">
                                    {/* Images */}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="aspect-square bg-muted rounded-xl overflow-hidden">
                                            {(selectedSuggestion.main_image_url || selectedSuggestion.image_url) ? (
                                                <img src={selectedSuggestion.main_image_url || selectedSuggestion.image_url} alt={selectedSuggestion.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Package className="w-16 h-16 text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                        {selectedSuggestion.gallery_images?.length > 0 && (
                                            <div className="grid grid-cols-3 gap-2">
                                                {selectedSuggestion.gallery_images.filter(img => img).slice(0, 6).map((img, i) => (
                                                    <div key={i} className="aspect-square bg-muted rounded-lg overflow-hidden">
                                                        <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Quick Info */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="bg-primary/10 rounded-lg p-3 text-center">
                                            <p className="text-xs text-muted-foreground">Suggested Price</p>
                                            <p className="text-lg font-bold text-primary">₹{selectedSuggestion.suggested_price?.toLocaleString()}</p>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <p className="text-xs text-muted-foreground">Brand</p>
                                            <p className="font-semibold">{selectedSuggestion.brand}</p>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <p className="text-xs text-muted-foreground">Size</p>
                                            <p className="font-semibold">{selectedSuggestion.system_size_kw} kW</p>
                                        </div>
                                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                                            <p className="text-xs text-muted-foreground">Warranty</p>
                                            <p className="font-semibold">{selectedSuggestion.warranty_years || 0} Years</p>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {(selectedSuggestion.short_description || selectedSuggestion.long_description) && (
                                        <div>
                                            <h3 className="font-semibold mb-2">Description</h3>
                                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                                {selectedSuggestion.short_description}
                                                {selectedSuggestion.long_description && <><br /><br />{selectedSuggestion.long_description}</>}
                                            </p>
                                        </div>
                                    )}

                                    {/* Features */}
                                    {selectedSuggestion.key_features?.filter(f => f).length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-2">Key Features</h3>
                                            <ul className="grid md:grid-cols-2 gap-2">
                                                {selectedSuggestion.key_features.filter(f => f).map((feature, i) => (
                                                    <li key={i} className="flex items-center gap-2 text-sm">
                                                        <Zap className="w-4 h-4 text-primary flex-shrink-0" />
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Specifications */}
                                    {selectedSuggestion.specifications?.filter(s => s.label && s.value).length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-2">Specifications</h3>
                                            <div className="grid md:grid-cols-2 gap-2">
                                                {selectedSuggestion.specifications.filter(s => s.label && s.value).map((spec, i) => (
                                                    <div key={i} className="flex justify-between bg-muted/30 rounded px-3 py-2 text-sm">
                                                        <span className="text-muted-foreground">{spec.label}</span>
                                                        <span className="font-medium">{spec.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Footer Actions */}
                                <div className="p-6 border-t flex gap-3">
                                    <Button
                                        variant="outline"
                                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={() => { handleRejectSuggestion(selectedSuggestion); setShowDetailModal(false); }}
                                    >
                                        <X className="w-4 h-4 mr-2" /> Reject
                                    </Button>
                                    <Button
                                        className="flex-1 bg-green-600 hover:bg-green-700"
                                        onClick={() => openSuggestionForEdit(selectedSuggestion)}
                                    >
                                        <Check className="w-4 h-4 mr-2" /> Review & Add to Catalog
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Approval Modal */}
                <AnimatePresence>
                    {showApprovalModal && selectedSuggestion && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApprovalModal(false)}>
                            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                                <div className="p-6 border-b">
                                    <h2 className="text-xl font-bold">Approve Product</h2>
                                    <p className="text-sm text-muted-foreground">{selectedSuggestion.name}</p>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <p className="text-sm text-muted-foreground">Vendor Suggested Price</p>
                                        <p className="text-xl font-bold">₹{selectedSuggestion.suggested_price?.toLocaleString()}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <IndianRupee className="w-4 h-4" /> Set SolarSavers Sell Price
                                        </Label>
                                        <Input
                                            type="number"
                                            value={sellPrice}
                                            onChange={(e) => setSellPrice(e.target.value)}
                                            placeholder="Enter sell price"
                                        />
                                        <p className="text-xs text-muted-foreground">This is the price customers will pay</p>
                                    </div>
                                    <div className="flex gap-3 pt-4 border-t">
                                        <Button variant="outline" onClick={() => setShowApprovalModal(false)} className="flex-1">Cancel</Button>
                                        <Button onClick={handleApproveSuggestion} className="flex-1 bg-green-600 hover:bg-green-700">
                                            <Check className="w-4 h-4 mr-2" /> Approve
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Category Modal */}
                <AnimatePresence>
                    {showCategoryModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCategoryModal(false)}>
                            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                                <div className="p-6 border-b flex items-center justify-between shrink-0">
                                    <h2 className="text-xl font-bold">Add Category</h2>
                                    <Button variant="ghost" size="icon" onClick={() => setShowCategoryModal(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>
                                <div className="overflow-y-auto flex-1 p-6">
                                    <form id="categoryForm" onSubmit={handleSaveCategory} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Category Name *</Label>
                                        <Input
                                            value={categoryForm.name}
                                            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                            placeholder="e.g., Solar Panels"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Slug</Label>
                                        <Input
                                            value={categoryForm.slug}
                                            onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                                            placeholder="solar-panels"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Description</Label>
                                        <Textarea
                                            value={categoryForm.description}
                                            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                            rows={2}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Parent Category</Label>
                                        <Select value={categoryForm.parent || 'none'} onValueChange={(v) => setCategoryForm({ ...categoryForm, parent: v === 'none' ? '' : v })}>
                                            <SelectTrigger><SelectValue placeholder="None (top-level)" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None (top-level)</SelectItem>
                                                {categories.map(cat => (
                                                    <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-4 pt-4 border-t">
                                        <div className="space-y-3">
                                            <Label className="text-base font-semibold">Standard Hardware Constraints</Label>
                                            <p className="text-xs text-muted-foreground pb-2">Toggle which rigid properties implicitly define this category of inventory</p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-2">
                                                <div className="flex items-center gap-2">
                                                    <Switch id="sys-size" checked={categoryForm.enable_system_size} onCheckedChange={(c) => setCategoryForm({ ...categoryForm, enable_system_size: c })} />
                                                    <Label htmlFor="sys-size">System Size (kW)</Label>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Switch id="eff-rating" checked={categoryForm.enable_efficiency} onCheckedChange={(c) => setCategoryForm({ ...categoryForm, enable_efficiency: c })} />
                                                    <Label htmlFor="eff-rating">Efficiency (%)</Label>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Switch id="warr-years" checked={categoryForm.enable_warranty} onCheckedChange={(c) => setCategoryForm({ ...categoryForm, enable_warranty: c })} />
                                                    <Label htmlFor="warr-years">Warranty (Years)</Label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3 pt-4 border-t">
                                        <div className="flex items-center justify-between">
                                            <Label>Custom Specifications</Label>
                                            <Button type="button" variant="outline" size="sm" onClick={() => setCategoryForm({ ...categoryForm, custom_fields: [...(categoryForm.custom_fields || []), { name: '', type: 'text', required: false }] })}>
                                                <Plus className="w-4 h-4 mr-1" /> Add Field
                                            </Button>
                                        </div>
                                        {categoryForm.custom_fields?.map((field, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-muted/50 p-2 rounded-lg">
                                                <Input placeholder="Field Name (e.g. Capacity)" value={field.name} onChange={(e) => {
                                                    const newFields = [...categoryForm.custom_fields];
                                                    newFields[i].name = e.target.value;
                                                    setCategoryForm({ ...categoryForm, custom_fields: newFields });
                                                }} className="flex-1 border-background" />
                                                <Select value={field.type} onValueChange={(v) => {
                                                    const newFields = [...categoryForm.custom_fields];
                                                    newFields[i].type = v;
                                                    setCategoryForm({ ...categoryForm, custom_fields: newFields });
                                                }}>
                                                    <SelectTrigger className="w-[110px] border-background"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="text">Text</SelectItem>
                                                        <SelectItem value="number">Number</SelectItem>
                                                        <SelectItem value="boolean">Yes/No</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => {
                                                    setCategoryForm({ ...categoryForm, custom_fields: categoryForm.custom_fields.filter((_, idx) => idx !== i) });
                                                }}><Trash2 className="w-4 h-4" /></Button>
                                            </div>
                                        ))}
                                    </div>
                                    </form>
                                </div>
                                <div className="p-6 border-t flex gap-3 shrink-0 bg-muted/10 rounded-b-2xl">
                                    <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)} className="flex-1">Cancel</Button>
                                    <Button type="submit" form="categoryForm" className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
                                        <Save className="w-4 h-4 mr-2" /> Save Category
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout >
    );
};

export default AdminProducts;
